// src/api/features/account/mutations.ts
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { updateMyAccount } from "./api";
import type { MyAccountResult, UpdateMyAccountBody } from "./types";
import { AccountKeys } from "./types";

/**
 * 🔹 Update the logged-in user's account/profile.
 *
 * - Optimistically patches the cached "me" data with the payload.
 * - Syncs with the server response on success.
 * - Rolls back if mutation fails.
 */
export function useUpdateMyAccount() {
  const qc = useQueryClient();
  const key: QueryKey = AccountKeys.me;

  return useMutation({
    mutationFn: (payload: UpdateMyAccountBody) => updateMyAccount(payload),

    async onMutate(payload) {
      await qc.cancelQueries({ queryKey: key });

      const prev = qc.getQueryData<MyAccountResult>(key);

      // Optimistic merge: shallow-merge payload into previous "me"
      if (prev) {
        qc.setQueryData<MyAccountResult>(key, {
          ...prev,
          ...payload,
        });
      }

      return { prev };
    },

    onError(_err, _payload, ctx) {
      if (ctx?.prev) {
        qc.setQueryData<MyAccountResult>(key, ctx.prev);
      }
    },

    onSuccess(updated) {
      // Ensure cache is synced with server response
      qc.setQueryData<MyAccountResult>(key, updated);
    },

    onSettled() {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}
